import successLotie from "assets/lotties/success.json";
import { InputField, Loading } from "components";
import Lottie from "lottie-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { AuthActivateAccount, signIn } from "store/slices/auth.slice";
import { verifyAccount } from "store/slices/email.slice";
import { getUserByCID } from "store/slices/user.slice";
import { IAuthActivateAccount } from "types/auth/auth.type";
import { PlatformBrandNameMap } from "utils/enums/platformBrandName";
import { SegmentEventENUM } from "utils/enums/segmentENUM";
import { cleanObject } from "utils/object";
import theme from "../../../assets/themes";
import Button from "../../../components/atoms/Button/Button";
import style from "./style.module.scss";

const SignupConfirmAccount: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { token } = useParams();
  const gatewayCustomerId = searchParams.get("cid");
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<IAuthActivateAccount>();
  const { isLoading, successMessage, errorMessage } = useAppSelector(
    (state) => state.auth.signUp
  );
  const { user } = useAppSelector((state) => state.auth);
  const { data } = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoadingInit, setIsLoadingInit] = useState(true);
  const handleSignUp = (data: IAuthActivateAccount) => {
    if (data.password !== data.passwordConfirm) {
      toast.error("Passwords do not match");
      return;
    }
    dispatch(
      AuthActivateAccount({
        password: data.password,
        gatewayCustomerId,
        token: token!,
      })
    );
    sendTrack();
  };
  const sendTrack = () => {
    const eventEnum = SegmentEventENUM.ACCOUNT_CREATED;
    const accountCreatedPayload = {
      firstname: data.firstName,
      lastname: data.lastName,
      email: data.email,
      organization: data.organizationName,
      clientId: data.clientId,
      brand: PlatformBrandNameMap[data.platformBrandId],
    };
    const cleanedFilters = cleanObject(accountCreatedPayload);
    window.analytics.track(eventEnum, cleanedFilters);
  };
  useEffect(() => {
    dispatch(verifyAccount(token || ""));
    dispatch(getUserByCID(gatewayCustomerId || "")).finally(() => {
      setIsLoadingInit(false);
    });

    window.analytics.page("Sign Up Confirmation");
    window.analytics.page("Email Verification");
  }, []);
  useEffect(() => {
    if (successMessage && data) {
      dispatch(
        signIn({
          email: data?.email,
          password: watch("password"),
          appScheme: "",
          segmentAnonymousId: "",
        })
      )
        .unwrap()
        .then((user) => {
          if (user.data.isOnboarded) {
            navigate(`/dashboard`);
            toast.success(successMessage);
            return;
          }
          navigate(`/onboarding`);
          toast.success(successMessage);
        });
    }
    if (errorMessage) toast.error(String(errorMessage));
  }, [successMessage, errorMessage, data]);
  if (data.hasVerifiedEmail && !gatewayCustomerId && !isLoadingInit) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 h-screen w-full m-auto">
        <Lottie
          animationData={successLotie}
          loop={false}
          width={40}
          className="max-w-[200px]"
        />
        <h1 className="font-bold capitalize text-2xl">Email verified</h1>
        <small className="text-gray-400 text-lg">
          Your email has been successfully validated, and you can now access
          your account.
        </small>
        <Link to="/signin" className=" mt-4">
          <Button
            size="sm"
            action="button"
            buttonContainerClassName="gap-4 flex-row-reverse"
            iconName="arrow-right"
            iconSize={16}
            type="primary"
          >
            Go to Login
          </Button>
        </Link>
      </div>
    );
  }
  return (
    <div
      className={`${style.signup_container} bg-primary sm:h-screen h-auto`}
      id="bg-container"
    >
      {isLoadingInit ? (
        <Loading background="transparent" />
      ) : (
        <>
          <div className={`${style.container_wrapper} bg-paper-500`}>
            <div className={style.form_wrapper_up}>
              <form
                onSubmit={handleSubmit(handleSignUp)}
                className={style.form}
              >
                <h1 className="text-2xl text-center font-bold">
                  ✅ Email Verified! Let's finish setting up your account.
                </h1>
                <p className="text-center">
                  <span className="text-base text-gray-300">
                    Please set a password so you can sign in to your account
                    safely.
                  </span>
                </p>
                <div className={style.inputs}>
                  <div className="flex gap-3 flex-col w-full">
                    <label className="font-bold">Create your password</label>
                    <InputField
                      iconName="lock"
                      type="password"
                      name="password"
                      errors={errors}
                      register={register}
                      maxLength={24}
                      validationSchema={{
                        required: "Password is required",
                        pattern: {
                          value:
                            /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$ %^&*-]).{8,}$/,
                          message:
                            "Minimum eight characters, at least one upper case English letter, one lower case English letter, one number and one special character",
                        },
                      }}
                    />
                  </div>
                  <div className="flex gap-3 flex-col w-full">
                    <label className="font-bold">Confirm your password</label>
                    <InputField
                      iconName="lock"
                      type="password"
                      name="passwordConfirm"
                      errors={errors}
                      register={register}
                      maxLength={24}
                    />
                  </div>
                </div>
                <p className="text-sm text-center text-gray-300">
                  By continuing, you agree with our{" "}
                  <Link
                    to={"/terms-of-service"}
                    target="_blank"
                    className="link"
                  >
                    Terms of Service
                  </Link>
                </p>
                <Button
                  type="primary"
                  action="submit"
                  size="lg"
                  className="w-full"
                  iconName="user-check"
                  isProcessing={isLoading}
                  isProcessingTitle="Activating account"
                >
                  Activate Account
                </Button>
                <div className="items-center flex flex-col">
                  <p className="text-sm text-gray-300 mb-3">
                    Already a member?
                  </p>
                  <Button
                    size="sm"
                    action="button"
                    onClick={() => navigate("/signin")}
                  >
                    Sign In
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
export default SignupConfirmAccount;
